import { type CouponType, DEFAULTS, type VatMode } from './constants'
import { AppError } from './errors'
import {
  applyPercent,
  type BasisPoints,
  type Money,
  splitVatExclusive,
  splitVatInclusive,
} from './money'

/**
 * FR-ORD-2 — the authoritative pricing engine.
 *
 * Every price that reaches this function must come from D1, never from a client
 * payload. The API worker resolves product/variant/modifier prices itself and
 * only then calls in here, so a tampered cart cannot change what is charged.
 */

export interface PriceableModifier {
  id: string
  /** May be negative (a discount modifier), but never below the item price. */
  priceDelta: Money
}

export interface PriceableLine {
  productId: string
  variantId: string | null
  /** Trusted unit price resolved from the database. */
  unitPrice: Money
  quantity: number
  modifiers: readonly PriceableModifier[]
}

export interface VatConfig {
  rateBp: BasisPoints
  mode: VatMode
}

export interface CouponRule {
  code: string
  type: CouponType
  /** Basis points for `percent`, integer BDT for `fixed`. */
  value: number
  minOrder?: Money
  maxDiscount?: Money
  startsAt?: number
  endsAt?: number
  usageLimit?: number
  usageCount?: number
  branchId?: string | null
  active?: boolean
}

export interface PricedLine extends PriceableLine {
  unitPriceWithModifiers: Money
  lineTotal: Money
}

export interface OrderTotals {
  lines: PricedLine[]
  subtotal: Money
  discount: Money
  /** Amount VAT is calculated on: subtotal - discount. */
  taxableAmount: Money
  vat: Money
  total: Money
}

export interface ComputeTotalsInput {
  lines: readonly PriceableLine[]
  coupon?: CouponRule | null
  vat?: VatConfig
}

export function computeOrderTotals({
  lines,
  coupon,
  vat = { rateBp: DEFAULTS.vatRateBp, mode: DEFAULTS.vatMode },
}: ComputeTotalsInput): OrderTotals {
  if (lines.length === 0) {
    throw new AppError('VALIDATION_FAILED', 'VALIDATION_FAILED: an order needs at least one item')
  }

  const priced: PricedLine[] = lines.map((line) => {
    if (
      !Number.isInteger(line.quantity) ||
      line.quantity < 1 ||
      line.quantity > DEFAULTS.maxLineQuantity
    ) {
      throw new AppError(
        'VALIDATION_FAILED',
        `VALIDATION_FAILED: quantity must be between 1 and ${DEFAULTS.maxLineQuantity}`,
        { productId: line.productId, quantity: line.quantity },
      )
    }

    const modifierTotal = line.modifiers.reduce((sum, modifier) => sum + modifier.priceDelta, 0)
    const unitPriceWithModifiers = line.unitPrice + modifierTotal

    if (unitPriceWithModifiers < 0) {
      throw new AppError(
        'VALIDATION_FAILED',
        'VALIDATION_FAILED: modifiers cannot reduce an item below zero',
        { productId: line.productId },
      )
    }

    return {
      ...line,
      unitPriceWithModifiers,
      lineTotal: unitPriceWithModifiers * line.quantity,
    }
  })

  const subtotal = priced.reduce((sum, line) => sum + line.lineTotal, 0)
  const discount = coupon ? computeDiscount(coupon, subtotal) : 0
  const taxableAmount = subtotal - discount

  const split =
    vat.mode === 'inclusive'
      ? splitVatInclusive(taxableAmount, vat.rateBp)
      : splitVatExclusive(taxableAmount, vat.rateBp)

  return {
    lines: priced,
    subtotal,
    discount,
    taxableAmount,
    vat: split.vat,
    total: split.gross,
  }
}

/** Discount never exceeds the subtotal, so a total can never go negative. */
export function computeDiscount(coupon: CouponRule, subtotal: Money): Money {
  const raw = coupon.type === 'percent' ? applyPercent(subtotal, coupon.value) : coupon.value
  const capped = coupon.maxDiscount != null ? Math.min(raw, coupon.maxDiscount) : raw
  return Math.max(0, Math.min(capped, subtotal))
}

export interface CouponContext {
  subtotal: Money
  now: number
  branchId?: string
}

/** FR-ORD-8 — throws an AppError whose code tells the client exactly what is wrong. */
export function validateCoupon(coupon: CouponRule, ctx: CouponContext): void {
  if (coupon.active === false) {
    throw new AppError('COUPON_INVALID', 'COUPON_INVALID: this coupon is not active')
  }

  if (coupon.branchId != null && ctx.branchId != null && coupon.branchId !== ctx.branchId) {
    throw new AppError('COUPON_INVALID', 'COUPON_INVALID: coupon is not valid at this branch')
  }

  if (coupon.startsAt != null && ctx.now < coupon.startsAt) {
    throw new AppError('COUPON_INVALID', 'COUPON_INVALID: coupon is not active yet', {
      startsAt: coupon.startsAt,
    })
  }

  if (coupon.endsAt != null && ctx.now > coupon.endsAt) {
    throw new AppError('COUPON_EXPIRED', 'COUPON_EXPIRED: coupon has expired', {
      endsAt: coupon.endsAt,
    })
  }

  if (coupon.minOrder != null && ctx.subtotal < coupon.minOrder) {
    throw new AppError('COUPON_MIN_ORDER', 'COUPON_MIN_ORDER: order is below the minimum', {
      minOrder: coupon.minOrder,
      subtotal: ctx.subtotal,
    })
  }

  if (coupon.usageLimit != null && (coupon.usageCount ?? 0) >= coupon.usageLimit) {
    throw new AppError('COUPON_EXHAUSTED', 'COUPON_EXHAUSTED: coupon usage limit reached')
  }
}

export interface WaitEstimateInput {
  /** Orders currently ahead of this one in the kitchen. */
  queueDepth: number
  avgPrepSeconds: number
  /** How many orders the kitchen can cook at once (steamer capacity). */
  parallelCapacity: number
}

/**
 * FR-ORD-9 — a deliberately simple model: how many cooking cycles deep the queue
 * is, plus one cycle for this order. Refined later with real prep telemetry.
 */
export function estimateWaitSeconds({
  queueDepth,
  avgPrepSeconds,
  parallelCapacity,
}: WaitEstimateInput): number {
  const capacity = Math.max(1, parallelCapacity)
  const cyclesAhead = Math.floor(Math.max(0, queueDepth) / capacity)
  return (cyclesAhead + 1) * avgPrepSeconds
}
